import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Loader2, Undo2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Kbd } from '@/components/ui/kbd';
import { DESTRUCTIVE_OUTLINE } from '../../../components/design-system/admin';
import { ColourSwatch } from '../../../components/design-system/common/ColourSwatch';
import { ProductImageTile } from '../../../components/design-system/common/ProductImageTile';
import { SizeLabel } from '../../../components/design-system/common/SizeLabel';
import type { LinkRow } from '../../../common/hooks/network-admin.types';
import type { NetworkLinkStatus } from '../../../common/models/network.model';
import { EvidenceChips, LinkStatusBadge, OverallScoreChip, ProductStatusBadge, methodLabel } from './network-parts';
import type { Thresholds } from './network-helpers';

/** Big enough to judge a surface by: the reviewer is comparing two photographs of the same tile. */
const IMAGE_EDGE = 240;

function Line({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex min-w-0 items-baseline gap-2 text-sm">
            <span className="w-20 shrink-0 text-xs text-muted-foreground">{label}</span>
            <span className="min-w-0 break-words">{children}</span>
        </div>
    );
}

/** One side of the comparison: a picture, then the facts a person checks. */
function Side({ heading, image, alt, name, children, testId }: { heading: ReactNode; image: string | null; alt: string; name: ReactNode; children: ReactNode; testId: string }) {
    const { t } = useTranslation();
    return (
        <div className="flex min-w-0 flex-col gap-3" data-testid={testId}>
            <p className="truncate text-xs text-muted-foreground">{heading}</p>
            <ProductImageTile src={image} alt={alt} size={IMAGE_EDGE} emptyLabel={t('ADMIN_NETWORK.COMMON.NO_IMAGE')} openLabel={t('ADMIN_NETWORK.COMMON.ENLARGE')} className="max-w-full" />
            <div className="min-w-0 text-base font-medium">{name}</div>
            <div className="flex flex-col gap-1">{children}</div>
        </div>
    );
}

export interface ReviewCardProps {
    link: LinkRow;
    focused: boolean;
    pending: boolean;
    thresholds: Thresholds;
    /** This card's decision can be undone with U (it is the last one made). */
    undoable: boolean;
    onFocus: () => void;
    onDecide: (status: NetworkLinkStatus) => void;
    onUndo: () => void;
}

/**
 * A matcher suggestion, side by side: the retailer's product on the start
 * side, the network product it may be on the end side, then every signal
 * the matcher used and the decision.
 */
export const ReviewCard = forwardRef<HTMLDivElement, ReviewCardProps>(function ReviewCard({ link, focused, pending, thresholds, undoable, onFocus, onDecide, onUndo }, ref) {
    const { t } = useTranslation();
    const r = link.retailer;
    const n = link.network;
    const decided = link.status !== 'suggested';

    return (
        // Card is not a forwardRef component: the wrapper carries the ref the queue scrolls to.
        <div ref={ref} className="scroll-mt-24" data-testid="review-row" data-link-id={link._id} data-status={link.status} aria-current={focused || undefined}>
        <Card
            onClick={onFocus}
            className={cn(
                'gap-4 px-5 py-5 transition-[box-shadow,opacity]',
                // The current card: a soft ink outline (Card draws its contour with a ring).
                focused && 'ring-[1.5px] ring-foreground/45',
                decided && !focused && 'opacity-70',
            )}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <OverallScoreChip score={link.score} />
                    <span className="text-sm text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.BY_METHOD', { method: methodLabel(t, link.method) })}</span>
                    {link.alternatives > 0 && (
                        <span className="text-xs text-muted-foreground" data-testid="alternatives">
                            {t('ADMIN_NETWORK.REVIEW.ALTERNATIVES', { count: link.alternatives })}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {pending && <Loader2 className="size-4 animate-spin text-muted-foreground" aria-label={t('ADMIN_NETWORK.COMMON.SAVING')} />}
                    <LinkStatusBadge status={link.status} />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Side
                    testId="review-retailer"
                    heading={t('ADMIN_NETWORK.REVIEW.RETAILER_SIDE', { vendor: link.vendor?.name || '—' })}
                    image={r?.imageUrl ?? null}
                    alt={r?.name || ''}
                    name={r ? r.name : <span className="text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.RETAILER_DELETED')}</span>}
                >
                    {r && (
                        <>
                            <Line label={t('ADMIN_NETWORK.FIELDS.SKU')}><span dir="ltr">{r.sku || '—'}</span></Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.BRAND')}>{r.brand || '—'}</Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.FORMAT')}><SizeLabel format={r.format} /></Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.COLOUR')}><ColourSwatch hex={r.colour?.hex} name={r.colour?.name} /></Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.EANS')}><span dir="ltr" className="tabular-nums">{r.eans?.length ? r.eans.join(', ') : '—'}</span></Line>
                        </>
                    )}
                </Side>
                <Side
                    testId="review-network"
                    heading={
                        n
                            ? [n.brand?.name || t('ADMIN_NETWORK.REVIEW.NO_BRAND'), n.collection?.name].filter(Boolean).join(' · ')
                            : t('ADMIN_NETWORK.REVIEW.NETWORK_SIDE')
                    }
                    image={n?.imageUrl ?? null}
                    alt={n?.name || ''}
                    name={
                        n ? (
                            <Link to={`/network/products/${n._id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                                {n.name}
                            </Link>
                        ) : (
                            <span className="text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.NETWORK_DELETED')}</span>
                        )
                    }
                >
                    {n && (
                        <>
                            <Line label={t('ADMIN_NETWORK.FIELDS.REF')}><span dir="ltr">{n.ref || '—'}</span></Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.STATUS')}>
                                <span className="inline-flex items-center gap-2">
                                    <ProductStatusBadge status={n.status} />
                                    <span className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.PRODUCTS.RETAILERS_N', { count: n.retailerCount ?? 0 })}</span>
                                </span>
                            </Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.FORMAT')}><SizeLabel format={n.format} /></Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.COLOUR')}><ColourSwatch hex={n.colour?.hex} name={n.colour?.name} /></Line>
                            <Line label={t('ADMIN_NETWORK.FIELDS.EANS')}><span dir="ltr" className="tabular-nums">{n.eans?.length ? n.eans.join(', ') : '—'}</span></Line>
                        </>
                    )}
                </Side>
            </div>

            <div className="flex flex-col gap-2 border-t pt-4">
                <EvidenceChips evidence={link.evidence} thresholds={thresholds} />
                {link.evidence?.notes && <p className="text-xs text-muted-foreground">{link.evidence.notes}</p>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    variant="outline"
                    disabled={pending || link.status === 'confirmed'}
                    onClick={(e) => {
                        e.stopPropagation();
                        // Hand the keyboard back to the queue (Enter on a focused button would press it).
                        e.currentTarget.blur();
                        onDecide('confirmed');
                    }}
                    data-testid="review-confirm"
                >
                    <Check />
                    {t('ADMIN_NETWORK.REVIEW.CONFIRM')}
                    {focused && <Kbd className="ms-1">C</Kbd>}
                </Button>
                <Button
                    variant="outline"
                    className={DESTRUCTIVE_OUTLINE}
                    disabled={pending || link.status === 'rejected'}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.currentTarget.blur();
                        onDecide('rejected');
                    }}
                    data-testid="review-reject"
                >
                    <X />
                    {t('ADMIN_NETWORK.REVIEW.REJECT')}
                    {focused && <Kbd className="ms-1">X</Kbd>}
                </Button>
                {decided && undoable && (
                    <Button
                        variant="ghost"
                        disabled={pending}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.currentTarget.blur();
                            onUndo();
                        }}
                        data-testid="review-undo"
                    >
                        <Undo2 />
                        {t('ADMIN_NETWORK.REVIEW.UNDO')}
                        <Kbd className="ms-1">U</Kbd>
                    </Button>
                )}
                {link.decidedBy && decided && <span className="ms-auto text-xs text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.DECIDED_BY', { who: link.decidedBy })}</span>}
            </div>
        </Card>
        </div>
    );
});
